import { computed, inject, type InjectionKey, ref, shallowRef, watch } from 'vue'
import { useRoute } from 'vue-router'

import type { UpdateBRMPerson } from '@/types/external/2work/api/request/UpdatePersonRequest'
import type {
  BRMPerson,
  CompleteOrganizationInfo,
} from '@/types/external/2work/doctypes/CustomDoctypes'

import { useOrganizationOptions } from '@/composables/useOrganizationOptions'
import { isDoctype } from '@/constants/doctypes'
import { PERMISSIONS } from '@/constants/permissions'
import { RelationType } from '@/constants/relations'
import { useFrappeNestCustomAPIStore } from '@/stores/apis/frappeNestCustomAPI'
import { useAuthStore } from '@/stores/auth'
import {
  sortAddresses,
  sortEmails,
  sortOrganizations,
  sortPhones,
} from '@/stores/relationships/storeHelperSortFunctions'
import { useSimpleNotificationStore } from '@/stores/simpleNotificationStore'
import { translationFunctionDefault } from '@/utilities/translations'
import { validateStringAgainstEnum } from '@/utilities/typeguards'

import { useRelationModals } from '../relations/useRelationModals'

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/explicit-module-boundary-types
const usePersonDetail = () => {
  const customAPIStore = useFrappeNestCustomAPIStore()
  const authStore = useAuthStore()
  const simpleNotificationStore = useSimpleNotificationStore()
  const tfDefault = translationFunctionDefault()
  const { organizationList, organizationOptions, refresh } = useOrganizationOptions()
  const route = useRoute()

  const { deleteModalOpen, setDeleteModalOpen } = useRelationModals()

  const person = shallowRef<BRMPerson>()
  const loading = ref(false)
  // Bumped after every successful save so the activity feed reloads,
  // matching the docSaved-driven reload of the Frappe detail iframe views.
  const activityReloadTrigger = ref(0)

  const completionRate = computed<number>(() => {
    const percentOffset = 100

    if (person.value?.tw_completion_rate !== undefined) {
      return person.value.tw_completion_rate * percentOffset
    }

    return 0
  })

  const linkedOrganizations = computed<CompleteOrganizationInfo[]>(() => {
    if (!person.value) {
      return []
    }
    const organizationLinks =
      person.value.links?.filter((link) => {
        return (
          !!link.link_doctype &&
          validateStringAgainstEnum(RelationType, link.link_doctype) !== undefined
        )
      }) ?? []

    if (organizationLinks.length === 0) {
      return []
    }

    const completeOrganizationInfoList: CompleteOrganizationInfo[] = []

    person.value.organizations.forEach((organization) => {
      const correspondOrgLink = organizationLinks.find((link) => {
        return link.link_name === organization.name
      })

      if (correspondOrgLink) {
        completeOrganizationInfoList.push({ link: correspondOrgLink, object: organization })
      }
    })

    return completeOrganizationInfoList.sort(sortOrganizations)
  })

  const primaryOrganization = computed<CompleteOrganizationInfo | undefined>(() => {
    return linkedOrganizations.value.find((org) => {
      return org.link.tw_is_primary_link === 1
    })
  })

  const fetchPerson = async (id: string): Promise<void> => {
    person.value = undefined
    await customAPIStore.getPersonDoc(id).then((newPerson) => {
      if (newPerson) {
        newPerson.email_ids.sort(sortEmails)
        newPerson.phone_nos.sort(sortPhones)
        newPerson.tw_addresses?.sort(sortAddresses(newPerson))

        // console.debug('new person', newPerson)
        person.value = newPerson
      } else {
        // TODO: handle error correctly
        throw new Error('wrong organization values')
      }
    })
  }

  const updatePerson = async (
    updateData: Omit<UpdateBRMPerson, 'doctype' | 'name'>,
  ): Promise<BRMPerson> => {
    if (!person.value) {
      throw new Error('person is not defined')
    }

    const { doctype } = person.value
    if (isDoctype(doctype) && !authStore.hasPermission(doctype, PERMISSIONS.WRITE)) {
      simpleNotificationStore.triggerSimpleNotificationError(
        tfDefault('editRelationGroup.editNoWritePermission'),
      )
      throw new Error('Permission denied: no write access')
    }

    return await customAPIStore
      .updatePersonList([
        {
          doctype: person.value.doctype,
          name: person.value.name,
          ...updateData,
        },
      ])
      .then((response) => {
        const personUpdate = response.find((responseItem) => {
          return responseItem.name === person.value?.name
        })

        if (personUpdate) {
          personUpdate.email_ids.sort(sortEmails)
          personUpdate.tw_addresses?.sort(sortAddresses(personUpdate))

          person.value = personUpdate
          activityReloadTrigger.value += 1

          return personUpdate
        }
        // TODO: handle error correctly
        throw new Error('person is not defined')
      })
  }

  watch(
    () => route.params['id'],
    async (id) => {
      if (typeof id === 'string') {
        loading.value = true
        console.debug('fetch person - route param watcher', id)
        await fetchPerson(id)
        loading.value = false
      }
    },
    { immediate: true },
  )

  return {
    activityReloadTrigger,
    completionRate,
    fetchPerson,
    linkedOrganizations,
    loading,
    modals: {
      deleteModalOpen,
      setDeleteModalOpen,
    },
    organizationList,
    organizationOptions,
    person,
    primaryOrganization,
    refreshOrganizationOptions: refresh,
    updatePerson,
  }
}

type PersonDetailContext = ReturnType<typeof usePersonDetail>

const personDetailContextInjectionKey: InjectionKey<PersonDetailContext> = Symbol(
  'personDetailContextInjectionKey',
)

const usePersonDetailContext = (): PersonDetailContext => {
  const context: PersonDetailContext | undefined = inject(personDetailContextInjectionKey)
  if (!context) {
    throw new Error('usePersonDetailContext must be used within a PersonDetailProvider')
  }
  return context
}

export {
  type PersonDetailContext,
  personDetailContextInjectionKey,
  usePersonDetail,
  usePersonDetailContext,
}
